/**
 * Challenge Service
 *
 * Handles Challenge Lab operations: fetching challenges, managing submissions,
 * and integrating with the structure analyzer.
 */

import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type {
  StoryChallenge,
  ChallengeSubmission,
  ChallengeSubmissionInsert,
  ChallengeDifficulty,
  AgeRange,
} from '@/types/database'
import { analyzeStructure } from './structureAnalyzer'
import { type StructureAnalysisResult, ANALYZER_VERSION } from '@/types/domain'
import { getWordCount } from '@/lib/textUtils'

// =============================================================================
// Challenge Queries
// =============================================================================

export interface ChallengeFilters {
  difficulty?: ChallengeDifficulty
  category?: string
  ageRange?: AgeRange
  featuredOnly?: boolean
}

/**
 * Get all published challenges, optionally filtered
 */
export async function getChallenges(
  filters?: ChallengeFilters
): Promise<StoryChallenge[]> {
  if (!isSupabaseConfigured()) return []

  let query = supabase
    .from('story_challenges')
    .select('*')
    .not('published_at', 'is', null)
    .is('deleted_at', null)
    .order('is_featured', { ascending: false })
    .order('created_at', { ascending: false })

  if (filters?.difficulty) {
    query = query.eq('difficulty', filters.difficulty)
  }

  if (filters?.category && filters.category !== 'all') {
    query = query.eq('category', filters.category)
  }

  if (filters?.ageRange) {
    query = query.contains('age_ranges', [filters.ageRange])
  }

  if (filters?.featuredOnly) {
    query = query.eq('is_featured', true)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching challenges:', error)
    throw error
  }

  return (data as StoryChallenge[]) || []
}

/**
 * Get a single challenge by ID
 */
export async function getChallenge(
  challengeId: string
): Promise<StoryChallenge | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('story_challenges')
    .select('*')
    .eq('id', challengeId)
    .not('published_at', 'is', null)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    console.error('Error fetching challenge:', error)
    throw error
  }

  return data as StoryChallenge
}

/**
 * Get featured challenges (for dashboard display)
 */
export async function getFeaturedChallenges(
  limit: number = 3
): Promise<StoryChallenge[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('story_challenges')
    .select('*')
    .eq('is_featured', true)
    .not('published_at', 'is', null)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('Error fetching featured challenges:', error)
    throw error
  }

  return (data as StoryChallenge[]) || []
}

// =============================================================================
// Submission Queries
// =============================================================================

/**
 * Get all submissions for a child
 */
export async function getSubmissionsForChild(
  childId: string
): Promise<ChallengeSubmission[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('challenge_submissions')
    .select('*')
    .eq('child_id', childId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching submissions:', error)
    throw error
  }

  return (data as ChallengeSubmission[]) || []
}

/**
 * Get submissions for a specific challenge by a child
 */
export async function getSubmissionsForChallenge(
  childId: string,
  challengeId: string
): Promise<ChallengeSubmission[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('challenge_submissions')
    .select('*')
    .eq('child_id', childId)
    .eq('challenge_id', challengeId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching challenge submissions:', error)
    throw error
  }

  return (data as ChallengeSubmission[]) || []
}

/**
 * Get a single submission by ID
 */
export async function getSubmission(
  submissionId: string
): Promise<ChallengeSubmission | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('challenge_submissions')
    .select('*')
    .eq('id', submissionId)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    console.error('Error fetching submission:', error)
    throw error
  }

  return data as ChallengeSubmission
}

/**
 * Get submission with its challenge data
 */
export async function getSubmissionWithChallenge(
  submissionId: string
): Promise<{ submission: ChallengeSubmission; challenge: StoryChallenge } | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('challenge_submissions')
    .select(`
      *,
      story_challenges (*)
    `)
    .eq('id', submissionId)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    console.error('Error fetching submission with challenge:', error)
    throw error
  }

  const submission = data as ChallengeSubmission & { story_challenges: StoryChallenge }

  return {
    submission: {
      ...submission,
      story_challenges: undefined,
    } as ChallengeSubmission,
    challenge: submission.story_challenges,
  }
}

// =============================================================================
// Submission Mutations
// =============================================================================

/**
 * Create a new draft submission for a challenge
 */
export async function createSubmission(
  childId: string,
  challengeId: string
): Promise<ChallengeSubmission> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const insert: ChallengeSubmissionInsert = {
    child_id: childId,
    challenge_id: challengeId,
    content: '',
    word_count: 0,
    status: 'draft',
  }

  const { data, error } = await supabase
    .from('challenge_submissions')
    .insert(insert as Record<string, unknown>)
    .select()
    .single()

  if (error) {
    console.error('Error creating submission:', error)
    throw error
  }

  return data as ChallengeSubmission
}

/**
 * Update submission character (optional character selection)
 */
export async function updateSubmissionCharacter(
  submissionId: string,
  characterId: string | null
): Promise<ChallengeSubmission> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const { data, error } = await supabase
    .from('challenge_submissions')
    .update({
      character_id: characterId,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', submissionId)
    .select()
    .single()

  if (error) {
    console.error('Error updating submission character:', error)
    throw error
  }

  return data as ChallengeSubmission
}

/**
 * Update submission content (auto-save while writing)
 */
export async function updateSubmissionContent(
  submissionId: string,
  content: string
): Promise<ChallengeSubmission> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const wordCount = getWordCount(content)

  const { data, error } = await supabase
    .from('challenge_submissions')
    .update({
      content,
      word_count: wordCount,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', submissionId)
    .select()
    .single()

  if (error) {
    console.error('Error updating submission:', error)
    throw error
  }

  return data as ChallengeSubmission
}

/**
 * Submit for analysis - runs the structure analyzer and saves results
 */
export async function submitForAnalysis(
  submissionId: string
): Promise<{ submission: ChallengeSubmission; analysis: StructureAnalysisResult }> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  // Get current submission
  const submission = await getSubmission(submissionId)
  if (!submission) {
    throw new Error('Submission not found')
  }

  // Run analysis
  const analysis = analyzeStructure(submission.content)

  // Update submission with analysis results
  const { data, error } = await supabase
    .from('challenge_submissions')
    .update({
      status: 'submitted',
      submitted_at: new Date().toISOString(),
      analysis_version: ANALYZER_VERSION,
      analysis_result: analysis,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', submissionId)
    .select()
    .single()

  if (error) {
    console.error('Error submitting for analysis:', error)
    throw error
  }

  return {
    submission: data as ChallengeSubmission,
    analysis,
  }
}

/**
 * Re-analyze an existing submission (e.g., after revisions)
 */
export async function reanalyzeSubmission(
  submissionId: string
): Promise<StructureAnalysisResult> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const submission = await getSubmission(submissionId)
  if (!submission) {
    throw new Error('Submission not found')
  }

  const analysis = analyzeStructure(submission.content)

  await supabase
    .from('challenge_submissions')
    .update({
      analysis_version: ANALYZER_VERSION,
      analysis_result: analysis,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', submissionId)

  return analysis
}

/**
 * Delete a submission (soft delete)
 */
export async function deleteSubmission(submissionId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const { error } = await supabase
    .from('challenge_submissions')
    .update({
      deleted_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', submissionId)

  if (error) {
    console.error('Error deleting submission:', error)
    throw error
  }
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a child has already submitted to a challenge today
 */
export async function hasSubmittedToday(
  childId: string,
  challengeId: string
): Promise<boolean> {
  if (!isSupabaseConfigured()) return false

  const today = new Date().toISOString().split('T')[0]

  const { data, error } = await supabase
    .from('challenge_submissions')
    .select('id')
    .eq('child_id', childId)
    .eq('challenge_id', challengeId)
    .eq('status', 'submitted')
    .eq('submission_date', today)
    .is('deleted_at', null)
    .limit(1)

  if (error) {
    console.error('Error checking today submission:', error)
    return false
  }

  return (data?.length ?? 0) > 0
}

/**
 * Get challenge categories (for filters)
 */
export async function getChallengeCategories(): Promise<string[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('story_challenges')
    .select('category')
    .not('published_at', 'is', null)
    .is('deleted_at', null)

  if (error) {
    console.error('Error fetching categories:', error)
    return []
  }

  const categories = new Set(
    (data as { category: string }[]).map(d => d.category)
  )
  return Array.from(categories).sort()
}

/**
 * Get submission history with challenge data
 */
export async function getSubmissionHistory(
  childId: string,
  limit: number = 20
): Promise<Array<ChallengeSubmission & { challenge: StoryChallenge }>> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('challenge_submissions')
    .select(`
      *,
      story_challenges (*)
    `)
    .eq('child_id', childId)
    .eq('status', 'submitted')
    .is('deleted_at', null)
    .order('submitted_at', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('Error fetching submission history:', error)
    throw error
  }

  return (data as Array<ChallengeSubmission & { story_challenges: StoryChallenge }>).map(item => ({
    ...item,
    challenge: item.story_challenges,
    story_challenges: undefined,
  })) as Array<ChallengeSubmission & { challenge: StoryChallenge }>
}
